import { useMemo } from 'react';
import { ConditionalRender } from '@ui';
import { TmdxResourceCard, ServiceWithoutCoordinatorCard } from '@features/components/ResourceCards';
import { MdOutlineWifiTethering } from 'react-icons/md';
import { WithDetachResourceModal } from '@features/components/CaseOverview/components/AssignedResources/WithDetachResourceModal';
import { getUserMenuItems } from '@features/components/CaseOverview/components/AssignedResources/userMenu.config';
import { useDetachAssignedUser } from '@features/components/CaseOverview/components/AssignedResources/hooks/useDetachAssignedUser';
import { AssignedUsersByGroup, CaseFacility, CaseUserGroup } from '@/api/apis/case';
import { getFullName, getRoleByGroup, getRolesFormatted, sortUsersByRole } from '@/utils/helpers';
import { Uuid } from '@/api/types';
import { TX_CENTER_CONTACTS_ROLES_WEIGHT } from '@/constants/case/case.constants';
import { ResourceSection } from '../ResourceSection';
import { NoContacts } from '../NoContacts';
import { CustomerModal } from '../CustomerModal';

type TxCenterContactsProps = {
  caseId: Uuid;
  assignedCustomers: AssignedUsersByGroup;
  txCenter: CaseFacility;
  menuDisabled: boolean;
};

export const TxCenterContacts = ({ caseId, assignedCustomers, menuDisabled, txCenter }: TxCenterContactsProps) => {
  const { handleDetach, isPendingDetach } = useDetachAssignedUser({ caseId });
  const txUsers = useMemo(() => {
    const admins = (assignedCustomers[CaseUserGroup.CustomerAdministrators] ?? []).map((u) => ({
      ...u,
      group: CaseUserGroup.CustomerAdministrators,
    }));
    const coordinators = (assignedCustomers[CaseUserGroup.CustomerCoordinators] ?? []).map((u) => ({
      ...u,
      group: CaseUserGroup.CustomerCoordinators,
    }));
    const surgeons = (assignedCustomers[CaseUserGroup.CustomerReceivingSurgeons] ?? []).map((u) => ({
      ...u,
      group: CaseUserGroup.CustomerReceivingSurgeons,
    }));
    const transplantSupport = (assignedCustomers[CaseUserGroup.CustomerTransplantSupporters] ?? []).map((u) => ({
      ...u,
      group: CaseUserGroup.CustomerTransplantSupporters,
    }));

    const all = [...admins, ...coordinators, ...surgeons, ...transplantSupport];

    return sortUsersByRole(all, TX_CENTER_CONTACTS_ROLES_WEIGHT);
  }, [assignedCustomers]);

  const coordinators = assignedCustomers[CaseUserGroup.CustomerCoordinators] ?? [];
  const hasCoordinator = coordinators.length > 0;

  return (
    <ResourceSection title="TX Center Contacts" count={txUsers.length + (hasCoordinator ? 0 : 1)}>
      <ConditionalRender show={txCenter && (txUsers.length > 0 || !hasCoordinator)} placeholder={<NoContacts />}>
        <>
          {!hasCoordinator && (
            <div className="w-80" key="service-without-tx-center-coordinator">
              <ServiceWithoutCoordinatorCard />
            </div>
          )}
          {txUsers.map(({ user, userId, group, customerId }) => {
            const fullName = getFullName(user.firstName, user.lastName);
            const filteredRoles = getRoleByGroup(user.roles, group);
            return (
              <div className="w-80" key={`${userId}_${group}`}>
                <WithDetachResourceModal
                  isPending={isPendingDetach}
                  onDetachSubmit={() => handleDetach({ customerId, group })}
                  photo={user.photoPath}
                  name={fullName}
                >
                  {({ onOpenChange }) => (
                    <CustomerModal
                      hospitalName={txCenter.name}
                      hospitalLabel="TX Center"
                      user={user}
                      customerId={customerId}
                      txCenterId={txCenter.id}
                    >
                      <TmdxResourceCard
                        icon={<MdOutlineWifiTethering className="text-base-20" />}
                        showChip={false}
                        list={getUserMenuItems({ onDetachPress: onOpenChange })}
                        menuDisabled={menuDisabled}
                        type="user"
                        title={fullName}
                        subTitle={getRolesFormatted(filteredRoles)}
                        phoneNumber={user.phone}
                        avatarProps={{ src: user.photoPath }}
                      />
                    </CustomerModal>
                  )}
                </WithDetachResourceModal>
              </div>
            );
          })}
        </>
      </ConditionalRender>
    </ResourceSection>
  );
};
